import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { MessageComposer } from "../../components/chatNewInterface/chatLayout/messageComposer/MessageComposer";

interface AttachedItem {
  id: string;
  name: string;
  duedate?: string | number | Date;
}

interface AttachedFile {
  id: string;
  fileName: string;
  fileURL?: string;
  isLoading?: boolean;
}

const generalSuggestions = [
  { text: "Plan my study schedule", color: "#e8ddfb", textColor: "#7244bb" },
  { text: "Create a study guide", color: "#d1e8eb", textColor: "#15727d" },
  { text: "Help with homework", color: "#fde8f0", textColor: "#c53469" },
];

const courseSuggestions = [
  { text: "What's due this week in this class?", color: "#e8ddfb", textColor: "#7244bb" },
  { text: "Explain this topic to me", color: "#d1e8eb", textColor: "#15727d" },
  { text: "Help me study for the next quiz", color: "#cce6ff", textColor: "#0069cc" },
  { text: "Summarize my latest assignment", color: "#fde8f0", textColor: "#c53469" },
];

const supportSuggestions = [
  { text: "When is the last day to withdraw?", color: "#e8ddfb", textColor: "#7244bb" },
  { text: "How do I apply for financial aid?", color: "#d1e8eb", textColor: "#15727d" },
  { text: "Where can I find tutoring services?", color: "#cce6ff", textColor: "#0069cc" },
  { text: "What are the add/drop deadlines?", color: "#fde8f0", textColor: "#c53469" },
];

interface ChatInputProps {
  value: string;
  onChangeText: (text: string) => void;
  onSubmit: () => void;

  disabled?: boolean;
  placeholder?: string;
  showSuggestions?: boolean;
  activeWorkspaceType: "general" | "course" | "support";

  // attachments (already computed)
  attachedTasks: AttachedItem[];
  attachedAssignments: AttachedItem[];
  attachedFiles: AttachedFile[];

  // actions
  onAddTask: () => void;
  onAddAssignment: () => void;
  onAddFile: () => void;
  onRemoveAttachment: () => void;

  // modals
  taskModal: React.ReactNode;
  assignmentModal: React.ReactNode;
}

const ChatInput = ({
  value,
  onChangeText,
  onSubmit,
  disabled,
  placeholder,
  showSuggestions = true,
  activeWorkspaceType,

  attachedTasks,
  attachedAssignments,
  attachedFiles,
  onAddTask,
  onAddAssignment,
  onAddFile,
  onRemoveAttachment,

  taskModal,
  assignmentModal,
}: ChatInputProps) => {
  const suggestedPrompts = useMemo(() => {
    if (!showSuggestions) return [];
    return activeWorkspaceType === "general"
      ? generalSuggestions
      : activeWorkspaceType === "course"
        ? courseSuggestions
        : supportSuggestions;
  }, [showSuggestions, activeWorkspaceType]);

  return (
    <View style={styles.container}>
      <MessageComposer
        disabled={disabled}
        message={value}
        setMessage={onChangeText}
        onSendMessage={onSubmit}
        placeholder={placeholder}
        suggestedPrompts={suggestedPrompts}
        addTask={onAddTask}
        addAssignment={onAddAssignment}
        addFile={onAddFile}
        removeTask={onRemoveAttachment}
        removeAssignment={onRemoveAttachment}
        removeFile={onRemoveAttachment}
        attachedTasks={attachedTasks}
        attachedAssignments={attachedAssignments}
        attachedFiles={attachedFiles}
      />

      {taskModal}
      {assignmentModal}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    paddingVertical: 0,
    paddingHorizontal: 0,
  },
});

export default ChatInput;
